import React from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, AlertCircle, CheckCircle} from 'lucide-react'

export default function ConditionsPage() {
  const conditions = [
    {
      name: 'Agoraphobia',
      description: 'Fear of places or situations that might cause panic, helplessness, or embarrassment',
      symptoms: ['Fear of leaving home alone', 'Fear of crowds or public spaces', 'Fear of enclosed spaces', 'Fear of open spaces', 'Fear of using public transportation'],
      prevalence: '1.7% of US adults (approximately 4 million people)'
    },
    {
      name: 'Panic Disorder',
      description: 'Recurring unexpected panic attacks with persistent worry about future attacks',
      symptoms: ['Sudden episodes of intense fear', 'Rapid heartbeat', 'Sweating and trembling', 'Shortness of breath', 'Fear of losing control or dying'],
      prevalence: '2.7% of US adults experience panic disorder annually'
    },
    {
      name: 'Social Phobia (Social Anxiety Disorder)',
      description: 'Intense fear of social situations where you might be judged or scrutinized by others',
      symptoms: ['Fear of speaking in public', 'Avoiding social gatherings', 'Fear of meeting new people', 'Worry about embarrassment', 'Physical symptoms in social settings'],
      prevalence: '7.1% of US adults affected each year'
    },
    {
      name: 'Specific Phobias',
      description: 'Excessive fear of specific objects or situations that poses little actual danger',
      symptoms: ['Immediate anxiety when exposed to feared object', 'Recognition that fear is excessive', 'Avoidance of the feared situation', 'Interferes with daily functioning'],
      prevalence: '12.5% of US adults will experience a specific phobia'
    },
    {
      name: 'Fear of Driving (Vehophobia)',
      description: 'Persistent anxiety about driving or being a passenger in a vehicle',
      symptoms: ['Panic when thinking about driving', 'Avoidance of highways or bridges', 'Physical symptoms while driving', 'Limitations on independence'],
      prevalence: 'Affects millions of Americans, often after accidents'
    },
    {
      name: 'Generalized Anxiety Disorder (GAD)',
      description: 'Persistent and excessive worry about various aspects of daily life',
      symptoms: ['Restlessness or feeling on edge', 'Fatigue and difficulty concentrating', 'Muscle tension', 'Sleep disturbances', 'Irritability'],
      prevalence: '3.1% of US adults (6.8 million people)'
    }
  ]

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5DADE2] to-[#48C9B0] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Types of Anxiety Disorders and Phobias
            </h1>
            <p className="text-xl text-blue-100">
              Understanding different anxiety conditions is the first step toward effective treatment and management.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-gray-700 mb-8">
            Anxiety disorders encompass several distinct conditions, each with unique symptoms and treatment approaches. While all involve excessive fear or worry, understanding your specific type of anxiety is crucial for effective treatment. Below is a comprehensive directory of the most common anxiety disorders and phobias affecting Americans today.
          </p>
        </div>
      </section>

      {/* Conditions Grid */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {conditions.map((condition, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md border border-gray-200 p-8 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start mb-4">
                  <AlertCircle className="w-8 h-8 text-[#4A90E2] mr-4 flex-shrink-0 mt-1" />
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                      {condition.name}
                    </h2>
                    <p className="text-lg text-gray-700 mb-4">{condition.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Common Symptoms</h3>
                    <ul className="space-y-2">
                      {condition.symptoms.map((symptom, idx) => (
                        <li key={idx} className="flex items-start">
                          <div className="w-2 h-2 bg-[#48C9B0] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-gray-700">{symptom}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Prevalence in USA</h3>
                    <p className="text-gray-700 mb-4">{condition.prevalence}</p>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <strong>Treatment Available:</strong> This condition is highly treatable through CBT, exposure therapy, and/or medication.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Focus: Agoraphobia */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Deep Dive: Understanding Agoraphobia
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            <strong>Agoraphobia</strong> is often misunderstood as simply a "fear of open spaces," but it's actually a complex anxiety disorder where individuals fear and avoid places or situations that might trigger panic attacks or make them feel trapped, helpless, or embarrassed.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            People with agoraphobia often avoid crowded places like shopping malls, public transportation, or even leaving their homes. This condition can severely limit independence and quality of life, but with proper treatment—particularly CBT and gradual exposure therapy—most individuals see significant improvement.
          </p>

          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Agoraphobia Treatment Approaches</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Cognitive Behavioral Therapy:</strong> Challenges catastrophic thinking patterns</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Gradual Exposure:</strong> Systematic desensitization to feared places</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Medication:</strong> SSRIs can reduce panic symptoms</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-700"><strong>Support Systems:</strong> Family involvement enhances recovery</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Identify Your Anxiety Type
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Take our GAD-7 screening to better understand your symptoms
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/assessment"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all shadow-lg"
            >
              Take the GAD-7 Quiz
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/therapy"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#48C9B0] text-white rounded-lg font-semibold hover:bg-[#3AB89E] transition-all shadow-lg"
            >
              Find a Therapist Near You
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
